import React from 'react';
import { Dialog, Stack, TextField, Button, 
  DialogActions, DialogContent, DialogTitle, MenuItem, Typography, Box, FormControlLabel, Checkbox} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {startOfWeek, addDays} from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';


import { useTranslation } from "react-i18next";

const SET_POINTS = ["16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30"];
const DEFAULT_VALUE = "21";


const EditDialog = (props) => {

  const {open, data, onClose, onChange, onSave, onDelete} = props;

  const { t } = useTranslation();

  const handleSelect = (field, value) => {
    onChange(field, value);
  }

  return (

    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("Edit temperature")}
      </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
              <TextField
                variant="standard"
                id="value"
                label={t("Value")}
                value={data.value}
                onChange={(event) => handleSelect("value", event.target.value)}
                select
              >
                {
                    SET_POINTS.map((item) => 
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  )
                }
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    id="preheat"
                    checked={data.preheat ?? false}
                    onChange={(event) => {
                      onChange("preheat", event.target.checked);
                    }}
                    style={{ paddingLeft: 0, paddingRight: 3 }}
                  />
                }
                label={t("Optimised start")}
              />
          </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave}>{t("Accept")}</Button>
        <Button onClick={() => onDelete()}>{t("Delete")}</Button>
        <Button onClick={onClose} autoFocus>{t("Cancel")}</Button>
      </DialogActions>
    </Dialog>

  )

}


const CalendarEditor = React.forwardRef((props, ref) => {

  const {
    schedule,
    onChange,
    onAdd,
    onDelete
  } = props;

  const { t, i18n } = useTranslation();

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editDialogData, setEditDialogData] = React.useState({});

  const calculateDate = (dayOfWeekISO) => {
    const t = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), dayOfWeekISO - 1)
    const day = ('0' + t.getDate()).slice(-2);
    const month = ('0' + (t.getMonth() + 1)).slice(-2);
    const year = t.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const calculateDatePlusOne = (dayOfWeekISO) => {
    const t = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), dayOfWeekISO )
    const day = ('0' + t.getDate()).slice(-2);
    const month = ('0' + (t.getMonth() + 1)).slice(-2);
    const year = t.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const events = schedule.map(
      (item) => (
        {
          id: item.id ?? uuidv4(),
          title: item.value + "°",
          start: calculateDate(item.iso_week_day ?? 1) + "T" + item.start,
          end: item.end !== "24:00"? calculateDate(item.iso_week_day ?? 1) + "T" + item.end : calculateDatePlusOne(item.iso_week_day ?? 1) + "T00:00:00",
          value: item.value
        }
      )
  )


  const toIsoWeekDay = (day) => {
    return (day + 6) % 7 + 1
  }

  const convertToSchedule = (item) => {
    return ({
        ...item,
        start: item.start.split("T")[1],
        end: item.end.split("T")[1] === "00:00"? "24:00" :item.end.split("T")[1],
        iso_week_day: toIsoWeekDay((new Date(item.start)).getDay())
      })
  }

  const handleEdit = (id, value) => {
    setEditDialogData({...editDialogData, [id]:value});
  }

  const handleSaveChanges = () => {

    if(schedule.some((item)=>item.id === editDialogData.id))
      onChange(editDialogData, []);
    else
      onAdd(editDialogData);

    setEditDialogOpen(false);
  }

  const toIsoString = (d) => {
    return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`
                     +`T${('0' + d.getHours()).slice(-2)}:${('0' + d.getMinutes()).slice(-2)}`;
  }

  const handleSelect = (selectInfo) => {

    let calendarApi = selectInfo.view.calendar;

    calendarApi.unselect()

    const start = selectInfo.start;
    const end = selectInfo.end;

    if (end.getHours() !== 0) {
      if ((end.getHours() < start.getHours()) || ((end.getHours() === start.getHours()) && (end.getMinutes() <= start.getMinutes()))){
        return;
      }
    }
      
    const newStart = toIsoString(start);

    const newEnd = (end.getHours() === 0) && (end.getMinutes() === 0) ? 
      `${addDays(start, 1).getFullYear()}-${('0' + (addDays(start, 1).getMonth() + 1)).slice(-2)}-${('0' + addDays(start, 1).getDate()).slice(-2)}`
      +`T00:00`
      :
      `${start.getFullYear()}-${('0' + (start.getMonth() + 1)).slice(-2)}-${('0' + start.getDate()).slice(-2)}`
      +`T${('0' + end.getHours()).slice(-2)}:${('0' + end.getMinutes()).slice(-2)}`
      ;


    const calendarEvent = {
      id: uuidv4(),
      start: newStart,
      end: newEnd,
      value: DEFAULT_VALUE
    }

    setEditDialogData(convertToSchedule(calendarEvent))

    setEditDialogOpen(true);
  }

  const handleEventChange = (data) => {

    const start = data.event.start;
    const end = data.event.end;

    if (end.getHours() !== 0) {
      if ((end.getHours() < start.getHours()) || ((end.getHours() === start.getHours()) && (end.getMinutes() <= start.getMinutes()))){
        return;
      }
    }

    const newStart = toIsoString(start);

    const newEnd = (end.getHours() === 0) && (end.getMinutes() === 0) ? 
      `${addDays(start, 1).getFullYear()}-${('0' + (addDays(start, 1).getMonth() + 1)).slice(-2)}-${('0' + addDays(start, 1).getDate()).slice(-2)}`
      +`T00:00`
      :
      `${start.getFullYear()}-${('0' + (start.getMonth() + 1)).slice(-2)}-${('0' + start.getDate()).slice(-2)}`
      +`T${('0' + end.getHours()).slice(-2)}:${('0' + end.getMinutes()).slice(-2)}`
      ;


    const modifiedEvent = convertToSchedule({...schedule.find((item)=>item.id === data.event.id), start: newStart, end: newEnd})

    const additionalEvents = []

    const diff = Math.floor((end - start) / 86400000);

    for (let i = 1; i <= diff; i++) {

      const extraStart = toIsoString(addDays(Date.parse(newStart), i));
      const extraEnd = toIsoString(addDays(Date.parse(newEnd), i));

      additionalEvents.push(
        convertToSchedule(
          {
            ...modifiedEvent,
            start: extraStart,
            end: extraEnd,
            id: uuidv4()
          }
        )
      )
    }

    onChange(modifiedEvent, additionalEvents)
    
  }

  const handleEventClick = (event) => {
    setEditDialogOpen(true);
    setEditDialogData(schedule.find((item)=>item.id === event.event.id));
  }

  const handleDelete = () => {

    onDelete(editDialogData.id)

    setEditDialogOpen(false);
  }

  return (
    <>
      <EditDialog 
        open={editDialogOpen} 
        data={editDialogData}
        onClose={()=>setEditDialogOpen(false)}
        onChange={handleEdit}
        onSave={handleSaveChanges}
        onDelete={handleDelete}
      />
      <Stack height="100%">
        <Box height={"100%"}>
          <FullCalendar
            height="100%"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView='timeGridWeek'
            headerToolbar={null}
            allDaySlot={false}
            events={events}
            weekNumberCalculation={"ISO"}
            editable={true}
            eventChange={handleEventChange}
            eventClick={handleEventClick}
            select={handleSelect}
            selectable={true}
            dayHeaderFormat={(d)=>{return (new Date(...d.date.array)).toLocaleString(i18n.resolvedLanguage, {weekday:'short'})}}
            now="2023-01-01" //to prevent the calendar from highlighting today's day
            initialDate={new Date()}
            eventOverlap={false}
          />
        </Box>    
      </Stack>
      
    </>
  )

  
})

export default CalendarEditor;